import React, { useEffect, useState } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { Button } from './ui/Button';
interface SlideMarketSizeProps {
  onContinue: () => void;
}
export function SlideMarketSize({
  onContinue
}: SlideMarketSizeProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 500);
    const t2 = setTimeout(() => setStage(2), 1300);
    const t3 = setTimeout(() => setStage(3), 2200);
    const t4 = setTimeout(() => setStage(4), 3000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, []);
  // Donut animation — animate the stroke-dashoffset
  const targetPct = 35;
  const radius = 90;
  const circumference = 2 * Math.PI * radius;
  const pctSpring = useSpring(0, {
    stiffness: 60,
    damping: 18
  });
  const dashOffset = useTransform(pctSpring, latest => circumference - circumference * latest / 100);
  const displayPct = useTransform(pctSpring, latest => `${Math.round(latest)}%`);
  useEffect(() => {
    if (stage >= 3) pctSpring.set(targetPct);
  }, [stage]);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-401">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-402">
        <h2 className="text-3xl md:text-5xl font-serif text-white mb-2" data-id="element-403">
          The hidden <span className="text-teal-400" data-id="element-404">revenue pool</span>
        </h2>
        <motion.div initial={{
        width: 0
      }} animate={{
        width: stage >= 1 ? 80 : 0
      }} transition={{
        duration: 0.6
      }} className="h-1 bg-teal-400 rounded-full" data-id="element-405" />
      </motion.div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mt-10" data-id="element-406">
        {/* LEFT — the two hero stats */}
        <div className="lg:col-span-7 space-y-12" data-id="element-407">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: stage >= 2 ? 1 : 0,
          y: stage >= 2 ? 0 : 20
        }} transition={{
          duration: 0.6
        }} data-id="element-408">
            <div className="text-6xl md:text-8xl font-serif text-teal-300 tracking-tight leading-none" data-id="element-409">
              30–35<span className="text-5xl md:text-7xl" data-id="element-410">%</span>
            </div>
            <p className="text-lg md:text-xl text-white/70 mt-4 max-w-md leading-snug" data-id="element-411">
              of big pharma revenue comes from{' '}
              <span className="text-white font-medium" data-id="element-412">
                mature / legacy brands.
              </span>
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: stage >= 3 ? 1 : 0,
          y: stage >= 3 ? 0 : 20
        }} transition={{
          duration: 0.6
        }} data-id="element-413">
            <div className="text-6xl md:text-8xl font-serif text-white tracking-tight leading-none" data-id="element-414">
              $180B–300B<span className="text-teal-400" data-id="element-415">+</span>
            </div>
            <p className="text-lg md:text-xl text-white/70 mt-4 max-w-md leading-snug" data-id="element-416">
              facing{' '}
              <span className="text-white font-medium" data-id="element-417">
                loss of exclusivity
              </span>{' '}
              in the coming years.
            </p>
          </motion.div>
        </div>

        {/* RIGHT — Donut chart */}
        <div className="lg:col-span-5 flex items-center justify-center" data-id="element-418">
          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: stage >= 3 ? 1 : 0,
          scale: stage >= 3 ? 1 : 0.8
        }} transition={{
          duration: 0.6
        }} className="relative" data-id="element-419">
            <svg width="280" height="280" viewBox="0 0 240 240" className="overflow-visible" data-id="element-420">
              {/* Background ring */}
              <circle cx="120" cy="120" r={radius} fill="none" stroke="#1E2F4A" strokeWidth="28" data-id="element-421" />
              {/* Active ring (animated) */}
              <motion.circle cx="120" cy="120" r={radius} fill="none" stroke="#00D4AA" strokeWidth="28" strokeLinecap="round" strokeDasharray={circumference} style={{
              strokeDashoffset: dashOffset
            }} transform="rotate(-90 120 120)" data-id="element-422" />
              {/* Soft outer glow */}
              <circle cx="120" cy="120" r={radius} fill="none" stroke="#00D4AA" strokeWidth="2" strokeOpacity="0.15" data-id="element-423" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-424">
              <motion.div className="text-4xl md:text-5xl font-serif text-teal-400" data-id="element-425">
                {displayPct}
              </motion.div>
              <div className="text-white/50 text-xs uppercase tracking-widest font-medium mt-1" data-id="element-426">
                Mature / Legacy
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom takeaway */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: stage >= 4 ? 1 : 0,
      y: stage >= 4 ? 0 : 10
    }} className="mt-8 flex items-center justify-between gap-6 bg-teal-400/5 border border-teal-400/20 rounded-xl px-6 py-4" data-id="element-427">
        <p className="text-white/80 leading-relaxed max-w-3xl" data-id="element-428">
          These brands represent a{' '}
          <span className="text-teal-400 font-semibold" data-id="element-429">
            massive, under-optimized segment
          </span>{' '}
          of pharma portfolios — the part that quietly funds the next generation
          of innovation.
        </p>
        <Button onClick={onContinue} data-id="element-430">The Problem</Button>
      </motion.div>
    </div>;
}